// force rebuild
import React, { useState, useEffect, useRef } from 'react';
import Login            from './pages/Login';
import LandingPage      from './pages/LandingPage';
import Register         from './pages/Register';
import ForgotPassword   from './pages/ForgotPassword';
import ResetPassword    from './pages/ResetPassword';
import SignupOrganizer  from './pages/SignupOrganizer';
import VerifyOrganizer  from './pages/VerifyOrganizer';
import AcceptTerms      from './pages/AcceptTerms';
import Leaderboard      from './pages/Leaderboard';
import BracketView      from './pages/BracketView';
import Admin            from './pages/Admin';
import PickSubmission   from './pages/PickSubmission';
import Navbar           from './components/Navbar';
import History from './pages/History';
import { getCurrentUser, logout, getOrganizerBySlug, setOnForbiddenHandler } from './services/api';
import FeedbackButton from './components/FeedbackButton';
import ImpersonationBanner from './components/ImpersonationBanner';
import BecomeOrganizer from './pages/BecomeOrganizer';
import AdminFeedback  from './pages/AdminFeedback';
import OrganizerMismatch from './components/OrganizerMismatch';
import { extractOrganizerSlug, isRootOrWwwHost, buildSubdomainUrl } from './lib/subdomain';
import { hasOrganizerAccess } from './lib/organizerAccess';

function App() {
    const [user, setUser] = useState(null);
    const [siteBranding, setSiteBranding] = useState(null); // { display_name, slug } | null -- null means platform branding
    // Only meaningful when siteBranding is null (i.e. not on an organizer
    // subdomain): true once a logged-out root/www visitor has clicked
    // "Log In" from the new marketing LandingPage, so we show <Login>
    // instead on the next render. Irrelevant on organizer subdomains,
    // which always show <Login> directly regardless of this flag -- see
    // the siteBranding check below.
    const [showLogin, setShowLogin] = useState(false);
    const [page,         setPage]         = useState('leaderboard');
    const [bracketTarget, setBracketTarget] = useState(null);
    // PR-JWT-COOKIE: auth state isn't synchronous anymore -- it requires
    // a round trip to GET /auth/me. authChecked guards against flashing
    // the Login screen during the initial fetch.
    const [authChecked, setAuthChecked] = useState(false);

    // When `user` is null, this controls which unauthenticated view renders.
    // 'login' (default) shows the Sign In form; 'register' shows the public
    // registration request form; 'forgot' shows the password-reset request
    // form. See PR-R1+R2 step 5 / admin guide 4.5, and PR-FP.
    const [authView, setAuthView] = useState('login');

    // PR-FP: once the user dismisses the /reset-password deep-link view,
    // this flag keeps them out of it even though the initial URL match
    // is computed every render. leaveResetPasswordRoute also rewrites the
    // URL, but the flag makes the transition immediate.
    const [resetRouteDismissed,    setResetRouteDismissed]    = useState(false);
    const [acceptTermsDismissed,   setAcceptTermsDismissed]   = useState(false);
    const [registerRouteDismissed, setRegisterRouteDismissed] = useState(false);

    // Organizer subdomain branding -- runs independently of auth state
    // (applies on the login screen too, before anyone's signed in). Falls
    // back to platform branding silently on any error (invalid/typo'd
    // slug, network issue, etc.) -- a broken subdomain should never break
    // the app, just show generic branding instead.
    useEffect(() => {
        const slug = extractOrganizerSlug(window.location.hostname);
        if (!slug) return;
        getOrganizerBySlug(slug)
            .then(res => setSiteBranding(res.data))
            .catch(() => { /* no matching organizer -- stay on platform branding */ });
    }, []);

    // PR-JWT-COOKIE: rehydrate session from the httpOnly cookie. If the
    // cookie is present and the token valid, /auth/me returns 200 with
    // the participant. If not, 401, and we stay on the login screen.
    // The old localStorage-decode-on-mount path is gone -- the cookie is
    // unreadable from JS by design.
    useEffect(() => {
        let cancelled = false;
        getCurrentUser()
            .then(res => {
                if (!cancelled) setUser(res.data.participant);
            })
            .catch(() => {
                // 401 = not logged in. No-op.
            })
            .finally(() => {
                if (!cancelled) setAuthChecked(true);
            });
        return () => { cancelled = true; };
    }, []);

    // Registers a handler that refetches /auth/me whenever ANY API call
    // anywhere in the app comes back 403 -- see the long comment on
    // setOnForbiddenHandler in services/api.js for why this exists. The
    // refetchInFlight ref is just a guard against a burst of several
    // simultaneous 403s (e.g. a page firing multiple requests at once)
    // triggering a pile of redundant overlapping /auth/me calls.
    const refetchInFlight = useRef(false);
    useEffect(() => {
        setOnForbiddenHandler(() => {
            if (refetchInFlight.current) return;
            refetchInFlight.current = true;
            getCurrentUser()
                .then(res => setUser(res.data.participant))
                .catch(() => {})
                .finally(() => { refetchInFlight.current = false; });
        });
        return () => setOnForbiddenHandler(null);
    }, []);

    // Auto-redirect: an organizer landing on the bare root/www domain
    // (rather than their own subdomain) gets bounced to it, since that's
    // meant to be their home surface, not the platform's generic
    // marketing/login page. Skipped for:
    //   - plain participants (organizer_slug is null -- nothing to
    //     redirect to)
    //   - platform admins (their workflows often need root-domain access
    //     for cross-organizer admin tasks -- same reasoning that exempts
    //     them from the mismatch guard below)
    //   - an impersonation session -- while impersonating, `user` IS the
    //     target organizer's own identity (is_platform_admin included,
    //     usually false for a normal organizer), so without this check a
    //     platform admin starting to impersonate any organizer from the
    //     root domain would get bounced to that organizer's subdomain --
    //     a real bug found in practice: the redirect actually fires,
    //     and since stopping impersonation doesn't navigate or re-fetch
    //     siteBranding (which is hostname-derived, fetched once on
    //     mount), the impersonated organizer's branding visibly "sticks"
    //     in the Navbar even after the session ends
    //   - anyone already on ANY organizer subdomain, including a
    //     mismatched one -- that's the OrganizerMismatch interstitial's
    //     job, not this effect's; this only fires on root/www
    //   - localhost/dev (isRootOrWwwHost only matches the real production
    //     hostnames, so this never fires locally)
    useEffect(() => {
        if (!authChecked || !user) return;
        if (user.is_platform_admin || user.impersonating || !user.organizer_slug) return;
        if (!isRootOrWwwHost(window.location.hostname)) return;
        window.location.href = buildSubdomainUrl(user.organizer_slug, window.location);
    }, [authChecked, user]);

    const handleLogin  = (participant) => setUser(participant);

    // Ends an impersonation session: the API call re-issues the real
    // admin's own cookie, and the response body is that admin's fresh
    // identity -- so we just swap it into user state directly, no
    // extra /auth/me round trip needed.
    const handleStopImpersonating = (participant) => setUser(participant);

    // After requesting organizer status, re-fetch /auth/me so the UI
    // immediately reflects the new pending_review status (organizer_status)
    // without needing a manual page reload.
    const handleOrganizerRequestSubmitted = () => {
        getCurrentUser().then(res => setUser(res.data.participant)).catch(() => {});
    };
    const handleLogout = async () => {
        try {
            await logout();
        } catch {
            // If the network call fails the cookie may still be set on
            // the server, but the user's intent is clear: clear local
            // state. Worst case they're logged in on next mount until
            // they explicitly log out from a working connection.
        }
        setUser(null);
        setAuthView('login');
    };

    // PR-FP: detect the password-reset deep link. The reset email points
    // at /reset-password?token=... -- since the app has no router, we
    // check the path here. This renders BEFORE the auth check and the
    // login screen: a user following the email link is not logged in,
    // and the reset page must not be pre-empted by either.
    const isResetPasswordRoute =
        window.location.pathname === '/reset-password';

    // Detect the organizer's shareable registration link: /register?org=N.
    // Like the reset-password route, this must render before the auth check
    // so unauthenticated participants land on the form immediately.
    // The org param is read from the URL here (before any navigation
    // changes window.location.search) and passed directly to Register.
    const isRegisterRoute =
        window.location.pathname === '/register' &&
        new URLSearchParams(window.location.search).has('org');
    const registerOrgId =
        new URLSearchParams(window.location.search).get('org');

    // Organizer self-signup deep links.
    // Direct deep-link to skip the marketing LandingPage and go straight
    // to Login -- used by the "Log In" nav link people might bookmark/
    // share, and by the Playwright suite (which predates the landing page
    // and assumes '/' reaches the login form directly).
    const isLoginRoute            = window.location.pathname === '/login';
    const isSignupOrganizerRoute  = window.location.pathname === '/signup-organizer';
    const isVerifyOrganizerRoute  = window.location.pathname === '/verify-organizer' &&
        new URLSearchParams(window.location.search).has('token');
    const isAcceptTermsRoute      = window.location.pathname === '/accept-terms' &&
        new URLSearchParams(window.location.search).has('token');

    // Leaving the reset page sends the user back to a clean login screen.
    // We replace the URL so a refresh doesn't re-trigger the reset route
    // (the token is single-use and likely already consumed).
    const leaveResetPasswordRoute = () => {
        window.history.replaceState({}, '', '/login');
        setAuthView('login');
        // Force a re-render now that the path no longer matches.
        setResetRouteDismissed(true);
    };

    if (isResetPasswordRoute && !resetRouteDismissed) {
        return <ResetPassword onDone={leaveResetPasswordRoute} />;
    }

    // Organizer signup — public, no auth required.
    if (isSignupOrganizerRoute) {
        const leaveSignup = () => {
            window.history.replaceState({}, '', '/login');
            setAuthView('login');
        };
        return <SignupOrganizer onBackToLogin={leaveSignup} />;
    }

    // Organizer email verification — auto-submits token on mount.
    if (isVerifyOrganizerRoute) {
        const handleVerified = (participant) => {
            window.history.replaceState({}, '', '/login');
            setUser(participant);
            setAuthView('login');
        };
        const leaveVerify = () => {
            window.history.replaceState({}, '', '/login');
            setAuthView('login');
        };
        return <VerifyOrganizer onVerified={handleVerified} onBackToLogin={leaveVerify} />;
    }

    // ToS/Privacy Policy acceptance — required after organizer approval
    // before full functional access. Does not auto-submit; requires the
    // person to actually check a box, unlike VerifyOrganizer.
    if (isAcceptTermsRoute && !acceptTermsDismissed) {
        const leaveAcceptTerms = () => {
            window.history.replaceState({}, '', '/login');
            setAuthView('login');
            // setAuthView('login') alone doesn't force a re-render here --
            // authView is already 'login' by default in the common case,
            // and React bails out of re-rendering on a same-value state
            // update. Same fix as resetRouteDismissed above: track
            // dismissal explicitly so isAcceptTermsRoute gets re-evaluated.
            setAcceptTermsDismissed(true);
        };
        return <AcceptTerms onBackToLogin={leaveAcceptTerms} />;
    }

    // Direct link to the registration form (e.g. from an organizer's invite).
    // Render Register immediately regardless of auth state — a logged-in
    // participant who follows a join link should still be able to see the
    // form and confirm which pools they're requesting access to.
    if (isRegisterRoute && !registerRouteDismissed) {
        const leaveRegisterRoute = () => {
            window.history.replaceState({}, '', '/login');
            setRegisterRouteDismissed(true);
        };
        return <Register orgId={registerOrgId} onBackToLogin={leaveRegisterRoute} />;
    }

    // Avoid flashing the Login screen for the brief moment between mount
    // and the /auth/me response.
    if (!authChecked) {
        return <div className="min-h-screen bg-gray-950" />;
    }

    // ToS/Privacy re-acceptance gate: an organizer whose tos_status isn't
    // 'current' (either 'outdated' -- ToS/Privacy changed since they last
    // accepted -- or 'never_accepted' -- hasn't gone through initial
    // acceptance at all) can log in fine but hits scattered 403s the
    // moment they try to do anything organizer-scoped server-side (see
    // attachOrganizer/_resolveOrganizer in middleware.js). Previously the
    // only way back in was an emailed link, which could be buried or
    // spam-filtered with no other path back into the app -- this shows a
    // blocking in-app gate instead, reusing the same AcceptTerms UI as
    // the email flow but backed by the session instead of a token. Checked
    // before organizerMismatch below since it's a more fundamental
    // blocker -- there's no point telling someone about a subdomain
    // mismatch if they can't use the app at all yet.
    if (user && user.organizer_id && user.tos_status && user.tos_status !== 'current') {
        return (
            <AcceptTerms
                sessionMode
                onAccept={() => {
                    // Update locally rather than waiting on a full
                    // /auth/me refetch -- the accept endpoint always sets
                    // the live combined version, so 'current' is correct
                    // immediately without needing to re-derive it.
                    setUser(prev => (prev ? { ...prev, tos_status: 'current', tos_accepted: true } : prev));
                }}
                onLogout={() => { setUser(null); setAuthView('login'); }}
            />
        );
    }

    // Organizer subdomain mismatch: a logged-in organizer is browsing a
    // DIFFERENT organizer's subdomain than their own. Plain participants
    // (organizer_slug === null), platform admins, and impersonation
    // sessions are never blocked -- see OrganizerMismatch.js for why
    // platform admins are exempt; impersonation gets the same exemption
    // for the same reason (while impersonating, `user.is_platform_admin`
    // is the TARGET organizer's own value, not the real admin's, so
    // without this check a platform admin impersonating organizer A
    // while browsing organizer B's subdomain would incorrectly hit this
    // interstitial instead of being allowed through like any other
    // legitimate cross-organizer admin task).
    // This is purely a branding/UX guard, not a data-scoping boundary --
    // every actual data-fetching route is already scoped server-side by
    // the JWT identity (see requireOwnership/_resolveOrganizer in
    // middleware.js), independent of which subdomain the request came
    // from. So this check lives entirely on the client, with values the
    // frontend already has from /auth/me and getOrganizerBySlug -- no
    // new backend endpoint needed.
    const organizerMismatch =
        !!user &&
        !user.is_platform_admin &&
        !user.impersonating &&
        !!siteBranding &&
        !!user.organizer_slug &&
        user.organizer_slug !== siteBranding.slug;

    if (organizerMismatch) {
        return (
            <OrganizerMismatch
                user={user}
                ownSlug={user.organizer_slug}
                onLogout={() => { setUser(null); setAuthView('login'); }}
            />
        );
    }

    if (!user) {
        if (authView === 'forgot') {
            return <ForgotPassword onBackToLogin={() => setAuthView('login')} />;
        }
        // siteBranding is only ever set when the current hostname resolved
        // to a real organizer subdomain (see the getOrganizerBySlug effect
        // above) -- so "no siteBranding" reliably means "not on an
        // organizer subdomain" for root/www AND local dev alike, without
        // needing hostname string-matching here. Organizer-subdomain
        // visitors always see Login directly (they almost always have a
        // specific invite link and specific intent, not "shopping around"
        // intent) -- the marketing page is reserved for root/www.
        if (!siteBranding && !showLogin && !isLoginRoute) {
            return (
                <LandingPage
                    onStartPool={() => {
                        window.history.pushState({}, '', '/signup-organizer');
                        setAuthView('signup-organizer');
                    }}
                    onLogin={() => setShowLogin(true)}
                />
            );
        }
        return (
            <Login
                onLogin={handleLogin}
                onForgotPassword={() => setAuthView('forgot')}
                siteBranding={siteBranding}
                onSignupOrganizer={() => {
                    window.history.pushState({}, '', '/signup-organizer');
                    // Use a distinct authView value so React sees a state
                    // change and re-renders. On the re-render, pathname is
                    // '/signup-organizer' so the isSignupOrganizerRoute
                    // early-return fires before authView is ever checked.
                    setAuthView('signup-organizer');
                }}
            />
        );
    }

    const tabs = [
        { id: 'leaderboard', label: 'Leaderboard',   show: true },
        { id: 'picks',       label: 'Submit Picks',   show: true },
        { id: 'bracket',     label: 'Bracket View',  show: true },
        { id: 'history',     label: 'History',        show: true },
        // Label reflects is_platform_admin only, not the legacy is_admin
        // flag -- someone with only is_admin set (pre-multi-tenancy
        // accounts) is not a platform admin as far as the backend is
        // concerned (see platformAdminOnly middleware), so showing "Admin"
        // for them is misleading. "Manage" covers both organizers and
        // legacy is_admin users who aren't true platform admins.
        // Label reflects is_platform_admin only, not the legacy is_admin
        // flag -- someone with only is_admin set (pre-multi-tenancy
        // accounts) is not a platform admin as far as the backend is
        // concerned. show requires hasOrganizerAccess(user) -- active +
        // ToS-accepted + (paid OR eval-plan waiver); see
        // lib/organizerAccess.js.
        { id: 'admin',       label: user.is_platform_admin ? 'Admin' : 'Manage', show: user.is_admin || user.is_platform_admin || hasOrganizerAccess(user) },
        { id: 'feedback',    label: 'Feedback',       show: user.is_platform_admin },
        // Hidden once truly an active organizer or platform admin (no need
        // to prompt them to become something they already are). Shown for
        // plain participants (organizer_status null), for pending/
        // suspended so they can see their request status or re-request,
        // and for active-but-not-yet-accepted so they can see the
        // "check your email" reminder. Hidden once hasOrganizerAccess(user)
        // (active + accepted + paid-or-eval) or platform admin.
        { id: 'become-organizer', label: 'Become an Organizer',
          show: !user.is_platform_admin && !hasOrganizerAccess(user) },
    ].filter(t => t.show);

    // While impersonating, the Navbar header should reflect the organizer
    // being impersonated -- not whichever organizer's subdomain the
    // platform admin happened to be physically browsing when they started
    // impersonating (siteBranding is purely hostname-derived and has no
    // knowledge of impersonation state). Without this override, the header
    // silently mismatches every other impersonation-aware piece of the
    // Admin Panel (tournament selector, role badge, Manage tab), which
    // would confuse anyone using impersonation for support/debugging.
    //
    // branding_colors rides along the same override: while impersonating,
    // colors come from the impersonated organizer's own saved colors
    // (user.branding_colors, from /auth/me under the impersonated
    // identity), same source as organizer_name/organizer_slug above.
    const effectiveBranding = (user.impersonating && user.organizer_name)
        ? { display_name: user.organizer_name, slug: user.organizer_slug, branding_colors: user.branding_colors }
        : siteBranding;

    // CSS custom properties consumed by components that want to reflect
    // an organizer's chosen branding (currently: organization name text
    // color in Navbar). Deliberately only set when effectiveBranding
    // carries real colors -- falls back to the app's default styling
    // (plain white/orange, no inline override) otherwise, same
    // subdomain-gated pattern already used for display_name itself.
    const brandingStyle = effectiveBranding?.branding_colors
        ? {
              '--org-primary': effectiveBranding.branding_colors.primary,
              '--org-accent':  effectiveBranding.branding_colors.accent,
          }
        : undefined;

    // Called by AdminAppSettings right after a successful branding
    // save/reset, so the Navbar (and anything else reading
    // effectiveBranding) reflects the change immediately in this same
    // session -- without this, the new colors are correctly saved to the
    // DB but siteBranding/user stay stale until a refresh or re-login,
    // since both are only fetched once on mount. Updates whichever of
    // the two sources effectiveBranding is actually drawing from: the
    // subdomain-derived siteBranding for normal viewing, or user's own
    // branding_colors for the (rarer) case of a platform admin editing
    // branding while actively impersonating that same organizer.
    const handleBrandingUpdated = (branding_colors) => {
        setSiteBranding(prev => (prev ? { ...prev, branding_colors } : prev));
        setUser(prev => (prev ? { ...prev, branding_colors } : prev));
    };

    return (
        <div className="min-h-screen bg-gray-950 text-white" style={brandingStyle}>
            <div className="fixed top-0 left-0 right-0 z-50 bg-gray-950">
                <ImpersonationBanner user={user} onStopImpersonating={handleStopImpersonating} />
                <Navbar user={user} onLogout={handleLogout} siteBranding={effectiveBranding} />
                <div className="bg-gray-900 border-b border-gray-800">
                    <div className="max-w-5xl mx-auto px-4 flex gap-1 pt-2">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setPage(tab.id)}
                                className={'px-4 py-2 text-sm font-bold rounded-t-lg transition-colors ' +
                                    (page === tab.id
                                        ? 'bg-gray-950 text-orange-400 border-t border-x border-gray-800'
                                        : 'text-gray-500 hover:text-gray-300')}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <div className={user.impersonating ? 'pt-32' : 'pt-24'}>
                {/* key={user.id} on every page: without it, impersonating
                    (or ending impersonation) swaps `user` but doesn't
                    remount already-mounted pages, so any component that
                    fetches its own data on mount (AdminParticipants,
                    AdminDashboard, etc.) keeps showing whatever it fetched
                    under the PREVIOUS identity -- stale data, not a scoping
                    bug. Forcing a remount on identity change guarantees
                    every page re-fetches fresh, correctly-scoped data. */}
                {page === 'leaderboard' && <Leaderboard key={user.id} user={user} onNavigate={(target) => {
                    setBracketTarget(target);
                    setPage('bracket');
                }} />}
                {page === 'picks'       && <PickSubmission key={user.id} user={user} />}
                {page === 'bracket'     && <BracketView key={user.id} user={user} initialTarget={bracketTarget} onTargetConsumed={() => setBracketTarget(null)} />}
                {page === 'history'     && <History key={user.id} />}
                {page === 'admin'       && <Admin key={user.id} user={user} onUserChange={handleStopImpersonating} onBrandingUpdated={handleBrandingUpdated} />}
                {page === 'feedback'    && <AdminFeedback key={user.id} />}
                {page === 'become-organizer' && <BecomeOrganizer key={user.id} user={user} onRequested={handleOrganizerRequestSubmitted} />}
            </div>
            <FeedbackButton page={page} />
        </div>
    );
}

export default App;